import { useEffect, useMemo, useRef, useState } from "react";
import { PhoneOverview } from "./PhoneOverview";
import type { AssignedWorkoutTree } from "../lib/assignedWorkoutTree";

/* W72 step 3 — THE BUILDER'S "Phone preview": the workout as the client's phone draws its overview (W95: the LIVE draft) — W66's own
 * `PhoneOverview`, never a second drawing. A second top-layer dialog beside the builder (the discard question's
 * shape), closed by its button, by Escape, or by clicking the dimmed ground around it. W95: the dialog is the device. */
export function PhoneWindow({ open, tree, onClose }: {
  readonly open: boolean;
  /** W95 — the builder's LIVE draft in the tree's shape (`draftTree`): nothing is saved before Save. */
  readonly tree: AssignedWorkoutTree | null;
  readonly onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  /** W130 — the walk-through is open inside the device: the stage bar says nothing is saved. */
  const [playing, setPlaying] = useState(false);
  /* W130 — the builder hands a NEW tree object on every render of its own (`draftTree(draft)` inline), and the overview
     re-reads on a new tree — which would throw away a walk in progress at the builder's next keystroke-free re-render. The
     tree is kept while its CONTENT is the same; an edit (impossible behind this modal dialog) would still be a new tree. */
  const sig = tree === null ? "" : JSON.stringify(tree);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableTree = useMemo(() => tree, [sig]);
  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);
  useEffect(() => { if (open) closeRef.current?.focus(); }, [open]);
  return (
    <dialog ref={dialogRef} className="ht-window wb-phone-window" aria-labelledby="wb-phone-title"
            onCancel={(e) => { e.preventDefault(); onClose(); }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* W95 (his word, 2026-09-29): the preview IS the phone — a rim with its side keys and the Dynamic Island around W66's
          frame, over the blurred scrim, and one small bar under it. No window surface around it. The keys and the island
          are drawings, hidden from assistive tech; the frame keeps its own `role="img"` label. */}
      <div className="phone-stage">
        <div className="phone-device">
          <span className="phone-key is-action" aria-hidden="true" />
          <span className="phone-key is-volume-up" aria-hidden="true" />
          <span className="phone-key is-volume-down" aria-hidden="true" />
          <span className="phone-key is-side" aria-hidden="true" />
          <span className="phone-key is-camera" aria-hidden="true" />
          {open && tree !== null && stableTree !== null && <PhoneOverview tree={stableTree} startable onPlayingChange={setPlaying} />}
          <span className="phone-island" aria-hidden="true" />
        </div>
        <div className="phone-stage-bar">
          <h2 className="xe-label" id="wb-phone-title">On the client&rsquo;s phone</h2>
          {/* W130 — said beside Close while the coach walks the workout: none of it reaches anyone. */}
          {open && playing && <p className="phone-preview-line" role="status">Preview — nothing is saved</p>}
          <button type="button" ref={closeRef} className="ht-button" onClick={onClose}>Close</button>
        </div>
      </div>
    </dialog>
  );
}
